import { useState, useEffect, useRef, useCallback } from 'react';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
    getWorkspaceSettingsURI,
    deleteWorkspaceSettingURI,
    validateDatadogCredentialURI,
    testDatadogConnectionURI,
    validateClaudeCredentialURI,
} from 'src/contexts/api';
import { logError } from 'src/utils/Error';

export interface IWorkspaceSettingOption {
    value: string
    label?: string
}

export interface IWorkspaceSetting {
    name: string
    label?: string
    kind?: string
    description?: string
    placeholder?: string
    sensitive?: boolean
    value?: string | null
    configured?: boolean
    valueHint?: string
    options?: IWorkspaceSettingOption[]
    defaultValue?: string
}

export interface IDatadogValidateRequest {
    apiKey: string
    site: string
}

export interface IDatadogTestConnectionResult {
    ok: boolean
    statusCode: number
    message: string
}

export interface IClaudeValidateRequest {
    apiKey: string
}

export interface IUseWorkspaceSettingsResult {
    settings: IWorkspaceSetting[]
    validateDatadogCredential: (workspaceId: string, body: IDatadogValidateRequest) => Promise<IDatadogTestConnectionResult>
    testDatadogConnection: (workspaceId: string) => Promise<IDatadogTestConnectionResult>
    validateClaudeCredential: (workspaceId: string, body: IClaudeValidateRequest) => Promise<IDatadogTestConnectionResult>
    deleteWorkspaceSetting: (workspaceId: string, name: string) => Promise<void>
}

const useWorkspaceSettings = (api: AxiosInstance, workspaceId: string | null, reloadSignal?: string | null): IUseWorkspaceSettingsResult => {
    const isMounted = useRef(false)
    useEffect(() => {
        isMounted.current = true
        return () => { isMounted.current = false }
    }, [])

    const [settings, setSettings] = useState<IWorkspaceSetting[]>([])

    useEffect(() => {
        if (!workspaceId || workspaceId === 'new') return
        api.get(getWorkspaceSettingsURI(workspaceId),
            { errorHandler: false } as AxiosRequestConfig)
            .then((resp: any) => {
                if (!isMounted.current) return
                // the API serialises setting fields as snake_case (e.g. `value_hint`); the
                // components read camelCase `valueHint`, so back-fill it here. Without this the
                // masked hint is always undefined and the card falls back to "Credential set".
                const normalised = (resp.data as any[]).map(s => ({
                    ...s,
                    valueHint: s.valueHint ?? s.value_hint,
                }))
                setSettings(normalised)
            })
            .catch((e: any) => {
                if (e?.response?.status !== 404) logError(e)
            })
        // reloadSignal changes on each successful save, so freshly-persisted values
        // (e.g. a sensitive setting's valueHint) are re-fetched without a page reload
    }, [workspaceId, reloadSignal])

    const validateDatadogCredential = useCallback(
        (id: string, body: IDatadogValidateRequest): Promise<IDatadogTestConnectionResult> =>
            api.post(validateDatadogCredentialURI(id), body, { errorHandler: false } as AxiosRequestConfig)
                .then((resp: any) => resp.data as IDatadogTestConnectionResult),
        [api])

    const testDatadogConnection = useCallback(
        (id: string): Promise<IDatadogTestConnectionResult> =>
            api.post(testDatadogConnectionURI(id), null, { errorHandler: false } as AxiosRequestConfig)
                .then((resp: any) => resp.data as IDatadogTestConnectionResult),
        [api])

    const validateClaudeCredential = useCallback(
        (id: string, body: IClaudeValidateRequest): Promise<IDatadogTestConnectionResult> =>
            api.post(validateClaudeCredentialURI(id), body, { errorHandler: false } as AxiosRequestConfig)
                .then((resp: any) => resp.data as IDatadogTestConnectionResult),
        [api])

    // Persist a single setting removal immediately (DELETE /settings/{name}), rather than
    // staging it for the workspace-form Save. A 404 means it was never persisted (e.g. an
    // unsaved staged key) — treat that as already-removed, not an error.
    const deleteWorkspaceSetting = useCallback(
        (id: string, name: string): Promise<void> =>
            api.delete(deleteWorkspaceSettingURI(id, name), { errorHandler: false } as AxiosRequestConfig)
                .then(() => undefined)
                .catch((e: any) => {
                    if (e?.response?.status === 404) return
                    throw e
                }),
        [api])

    return { settings, validateDatadogCredential, testDatadogConnection, validateClaudeCredential, deleteWorkspaceSetting }
}

export default useWorkspaceSettings
